import {
  IMAGES_LINK_MAX_LENGTH,
  IMAGING_EXAM_STATES,
  IMAGING_MODALITIES,
  IMAGING_MODALITY_LABELS,
  IMAGING_REPORT_STATUSES,
  isImagesLink,
  type ImagingExamState,
} from '@wodi/domain';
import { z } from 'zod';

import { actorSchema, reasonSchema } from './clinical-common.ts';
import { LAB_GATE_STATES, labPatientSchema } from './lab.ts';
import { shaApprovalSchema } from './sha-approval.ts';

// Radiology RIS lite (docs/plans/phase2-radiology.md §3). The order is the Phase 1 imaging
// request; these are the worklist, the exam, the versioned report and Settings > Imaging.
// Clinical questions, notes, findings, impressions and links are PHI: bodies only.

export { IMAGING_EXAM_STATES, IMAGING_MODALITIES, IMAGING_MODALITY_LABELS };
export type { ImagingExamState };

/** The worklist's five lists, in the order work flows. */
export const IMAGING_WORKLIST_STATES = [
  'requested',
  'scheduled',
  'performed',
  'reported',
  'verified',
] as const;
export type ImagingWorklistState = (typeof IMAGING_WORKLIST_STATES)[number];

/** One word for each state, the same on the worklist, the exam and the consultation. */
export const IMAGING_EXAM_STATE_LABELS: Record<ImagingExamState, string> = {
  requested: 'Requested',
  scheduled: 'Scheduled',
  performed: 'Performed, not reported',
  reported: 'Reported, not verified',
  verified: 'Verified',
  cancelled: 'Cancelled',
  entered_in_error: 'Entered in error',
};

export const imagesLinkSchema = z
  .string()
  .trim()
  .max(IMAGES_LINK_MAX_LENGTH)
  .refine(isImagesLink, 'Paste the full address, starting with https:// or http://');

export const imagingWorklistQuerySchema = z.object({
  state: z.enum(IMAGING_WORKLIST_STATES).default('requested'),
});

export const imagingWorklistRowSchema = z.object({
  orderId: z.uuid(),
  visitId: z.uuid(),
  patient: labPatientSchema,
  itemName: z.string(),
  itemCode: z.string(),
  /** Null when Settings > Imaging doesn't say. */
  modality: z.enum(IMAGING_MODALITIES).nullable(),
  priority: z.enum(['routine', 'urgent']),
  state: z.enum(IMAGING_EXAM_STATES),
  orderedAt: z.iso.datetime(),
  scheduledFor: z.iso.datetime().nullable(),
  performedAt: z.iso.datetime().nullable(),
  reportedAt: z.iso.datetime().nullable(),
  verifiedAt: z.iso.datetime().nullable(),
  minutesWaiting: z.number().int(),
  /** Whether it can be done yet: pay-before-service and SHA approval, in the lab's words. */
  gate: z.enum(LAB_GATE_STATES),
  shaApproval: shaApprovalSchema.nullable(),
});
export type ImagingWorklistRow = z.infer<typeof imagingWorklistRowSchema>;

export const imagingWorklistSchema = z.object({ items: z.array(imagingWorklistRowSchema) });

export const imagingReportVersionSchema = z.object({
  version: z.number().int().min(1),
  findings: z.string().nullable(),
  impression: z.string(),
  signedBy: actorSchema,
  signedAt: z.iso.datetime(),
  amendmentReason: z.string().nullable(),
});
export type ImagingReportVersionView = z.infer<typeof imagingReportVersionSchema>;

/** A report with every version, newest first; `current` is the one in force. */
export const imagingReportSchema = z.object({
  id: z.uuid(),
  orderId: z.uuid(),
  visitId: z.uuid(),
  patientId: z.uuid(),
  itemName: z.string(),
  itemCode: z.string(),
  modality: z.enum(IMAGING_MODALITIES).nullable(),
  performedAt: z.iso.datetime().nullable(),
  orderingUser: actorSchema,
  status: z.enum(IMAGING_REPORT_STATUSES),
  /** Entered in error: the exam was withdrawn; the report stays readable, marked so. */
  enteredInError: z.object({ reason: z.string() }).nullable(),
  currentVersion: z.number().int().min(1),
  verifiedBy: actorSchema.nullable(),
  verifiedAt: z.iso.datetime().nullable(),
  current: imagingReportVersionSchema,
  versions: z.array(imagingReportVersionSchema),
});
export type ImagingReportView = z.infer<typeof imagingReportSchema>;

export const imagingProcedureSchema = z.object({
  serviceItemId: z.uuid(),
  code: z.string(),
  name: z.string(),
  itemStatus: z.enum(['active', 'inactive']),
  /** Null until Settings > Imaging sets it. */
  modality: z.enum(IMAGING_MODALITIES).nullable(),
  bodyPart: z.string().nullable(),
  preparation: z.string().nullable(),
  reportTemplate: z.string().nullable(),
});
export type ImagingProcedureView = z.infer<typeof imagingProcedureSchema>;

export const imagingProceduresSchema = z.object({ items: z.array(imagingProcedureSchema) });

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullable()
    .transform((v) => (v === null || v === '' ? null : v));

export const updateImagingProcedureRequestSchema = z.object({
  modality: z.enum(IMAGING_MODALITIES),
  bodyPart: optionalText(80),
  preparation: optionalText(1000),
  reportTemplate: optionalText(4000),
});
export type UpdateImagingProcedureRequest = z.input<typeof updateImagingProcedureRequestSchema>;

export const imagingExamSchema = z.object({
  orderId: z.uuid(),
  visitId: z.uuid(),
  visitStatus: z.string(),
  patient: labPatientSchema,
  itemName: z.string(),
  itemCode: z.string(),
  modality: z.enum(IMAGING_MODALITIES).nullable(),
  bodyPart: z.string().nullable(),
  preparation: z.string().nullable(),
  reportTemplate: z.string().nullable(),
  priority: z.enum(['routine', 'urgent']),
  /** The clinical question. */
  reason: z.string().nullable(),
  orderingUser: actorSchema,
  orderedAt: z.iso.datetime(),
  state: z.enum(IMAGING_EXAM_STATES),
  statusReason: z.string().nullable(),
  gate: z.enum(LAB_GATE_STATES),
  shaApproval: shaApprovalSchema.nullable(),
  scheduledFor: z.iso.datetime().nullable(),
  scheduleNote: z.string().nullable(),
  scheduledBy: actorSchema.nullable(),
  performedAt: z.iso.datetime().nullable(),
  performedBy: actorSchema.nullable(),
  /** The radiographer's note (Phase 1's outcome text). */
  performedNote: z.string().nullable(),
  imagesLink: z.string().nullable(),
  report: imagingReportSchema.nullable(),
});
export type ImagingExamView = z.infer<typeof imagingExamSchema>;

export const scheduleImagingRequestSchema = z.object({
  scheduledFor: z.iso.datetime(),
  note: optionalText(200).default(null),
});
export type ScheduleImagingRequest = z.input<typeof scheduleImagingRequestSchema>;

export const performImagingRequestSchema = z.object({
  performedAt: z.iso.datetime().optional(),
  note: optionalText(20_000).default(null),
  imagesLink: imagesLinkSchema.nullable().default(null),
});
export type PerformImagingRequest = z.input<typeof performImagingRequestSchema>;

export const imagesLinkRequestSchema = z.object({ imagesLink: imagesLinkSchema.nullable() });

const findingsSchema = optionalText(20_000);
const impressionSchema = z.string().trim().min(1).max(4000);

export const signImagingReportRequestSchema = z.object({
  clientRequestId: z.uuid(),
  findings: findingsSchema.default(null),
  impression: impressionSchema,
  /** Sign and verify in one step; needs `imaging.report.verify` (plan R7). */
  verify: z.boolean().default(false),
});
export type SignImagingReportRequest = z.input<typeof signImagingReportRequestSchema>;

export const amendImagingReportRequestSchema = signImagingReportRequestSchema.extend({
  reason: reasonSchema,
  /** The version the amender started from: a newer one is refused, so no change is lost. */
  baseVersion: z.number().int().min(1),
});
export type AmendImagingReportRequest = z.input<typeof amendImagingReportRequestSchema>;

/** The version the verifier read: a newer one is refused, so nobody verifies unseen text. */
export const verifyImagingReportRequestSchema = z.object({
  version: z.number().int().min(1),
});

export const imagingReportPrintedSchema = z.object({
  copy: z.number().int().min(1).max(100),
  version: z.number().int().min(1),
});

export const patientImagingReportsSchema = z.object({ items: z.array(imagingReportSchema) });
